import React, { useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import "../styles/PolicyManager.css";

// ===============================
// GraphQL Queries & Mutations (with termsUrl)
// ===============================

const GET_ALL_TERMS = gql`
  query GetAllTerms($type: TermsType) {
    getAllTerms(type: $type) {
      _id
      type
      title
      contentText
      termsUrl
      version
      isActive
      createdAt
      updatedAt
    }
  }
`;

const GET_ACTIVE_TERMS = gql`
  query GetActiveTerms($type: TermsType!) {
    getActiveTerms(type: $type) {
      _id
      type
      title
      contentText
      termsUrl
      version
      createdAt
    }
  }
`;

const CREATE_TERMS = gql`
  mutation CreateTerms(
    $type: TermsType!
    $title: String
    $contentText: String
    $termsUrl: String
  ) {
    createTerms(
      type: $type
      title: $title
      contentText: $contentText
      termsUrl: $termsUrl
    ) {
      _id
      type
      title
      contentText
      termsUrl
      version
      isActive
      createdAt
    }
  }
`;

const UPDATE_TERMS = gql`
  mutation UpdateTerms(
    $id: ID!
    $title: String
    $contentText: String
    $isActive: Boolean
    $termsUrl: String
  ) {
    updateTerms(
      id: $id
      title: $title
      contentText: $contentText
      isActive: $isActive
      termsUrl: $termsUrl
    ) {
      _id
      type
      title
      contentText
      termsUrl
      version
      isActive
      updatedAt
    }
  }
`;

const DELETE_TERMS = gql`
  mutation DeleteTerms($id: ID!) {
    deleteTerms(id: $id)
  }
`;

// ===============================
// Date Formatting Utility
// ===============================

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

// ===============================
// Policy Configuration - 5 Headings
// ===============================

const ALL_POLICIES = [
  {
    id: "PRIVACY_POLICY",
    label: "Privacy",
    fullLabel: "Privacy Policy",
    icon: "🔒",
    color: "#0891B2",
    lightColor: "#ECFEFF",
    badge: "General",
  },
  {
    id: "SELLER_SHIPPING_POLICY",
    label: "Seller Shipping",
    fullLabel: "Seller Shipping Policy",
    icon: "🛍️🚚",
    color: "#B45309",
    lightColor: "#FFF7ED",
    badge: "Seller",
  },
  {
    id: "SELLER_RETURN_POLICY",
    label: "Seller Returns",
    fullLabel: "Seller Return Policy",
    icon: "🛍️🔄",
    color: "#A21C39",
    lightColor: "#FEF2F4",
    badge: "Seller",
  },
  {
    id: "BUYER_SHIPPING_POLICY",
    label: "Buyer Shipping",
    fullLabel: "Buyer Shipping Policy",
    icon: "🛒🚚",
    color: "#059669",
    lightColor: "#ECFDF5",
    badge: "Buyer",
  },
  {
    id: "BUYER_RETURN_POLICY",
    label: "Buyer Returns",
    fullLabel: "Buyer Return Policy",
    icon: "🛒🔄",
    color: "#2563EB",
    lightColor: "#EFF6FF",
    badge: "Buyer",
  }
];

// ===============================
// Main Component
// ===============================

function PolicyManager() {
  const [activePolicyId, setActivePolicyId] = useState("PRIVACY_POLICY");
  const [title, setTitle] = useState("");
  const [termsUrl, setTermsUrl] = useState("");
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });

  const currentPolicy = ALL_POLICIES.find(p => p.id === activePolicyId) || ALL_POLICIES[0];

  // Queries
  const { loading, error, data, refetch } = useQuery(GET_ALL_TERMS, {
    variables: { type: activePolicyId },
    fetchPolicy: "network-only",
    skip: !activePolicyId,
  });

  const { data: activeData } = useQuery(GET_ACTIVE_TERMS, {
    variables: { type: activePolicyId },
    fetchPolicy: "network-only",
    skip: !activePolicyId,
  });

  const [createTerms] = useMutation(CREATE_TERMS);
  const [updateTerms] = useMutation(UPDATE_TERMS);
  const [deleteTerms] = useMutation(DELETE_TERMS);

  // Utilities
  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: "", type: "" }), 3000);
  };

  const resetForm = () => {
    setTitle("");
    setTermsUrl("");
    setEditId(null);
  };

  // Submit handler – only URL mode
  const handleSubmit = async (e) => {
    e.preventDefault();

    const url = termsUrl.trim();
    if (!url) {
      showNotification("Please provide a valid URL!", "error");
      return;
    }

    // Backend expects contentText, we send a placeholder (single space)
    const finalContent = " "; 
    const finalUrl = url;

    try {
      if (editId) {
        await updateTerms({
          variables: {
            id: editId,
            title: title.trim() || undefined,
            contentText: finalContent,
            isActive: true,
            termsUrl: finalUrl,
          },
        });
        showNotification(`${currentPolicy.fullLabel} updated successfully`, "success");
      } else {
        await createTerms({
          variables: {
            type: activePolicyId,
            title: title.trim() || currentPolicy.fullLabel,
            contentText: finalContent,
            termsUrl: finalUrl,
          },
        });
        showNotification(`${currentPolicy.fullLabel} created successfully`, "success");
      }
      resetForm();
      refetch();
    } catch (err) {
      showNotification(err.message, "error");
    }
  };

  // CRUD Handlers
  const handleEdit = (term) => {
    setEditId(term._id);
    setTitle(term.title || "");
    setTermsUrl(term.termsUrl || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this policy document?")) return;
    try {
      await deleteTerms({ variables: { id } });
      showNotification(`${currentPolicy.fullLabel} document deleted`, "success");
      refetch();
    } catch (err) {
      showNotification(err.message, "error");
    }
  };

  const handleSetActive = async (id) => {
    try {
      await updateTerms({ variables: { id, isActive: true } });
      showNotification(`${currentPolicy.fullLabel} activated`, "success");
      refetch();
    } catch (err) {
      showNotification(err.message, "error");
    }
  };

  const handlePolicyChange = (policyId) => {
    setActivePolicyId(policyId);
    resetForm();
    setSearchTerm("");
  };

  // Filter terms
  const terms = data?.getAllTerms || [];
  const activeTerms = activeData?.getActiveTerms;
  const filteredTerms = terms.filter((term) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      term.title?.toLowerCase().includes(searchLower) ||
      term.contentText?.toLowerCase().includes(searchLower) ||
      term.version?.toString().includes(searchLower)
    );
  });

  // Loading / Error
  if (loading) return (
    <div className="policy-loading">
      <div className="loading-spinner" style={{ borderTopColor: currentPolicy.color }}></div>
      <p>Loading {currentPolicy.label.toLowerCase()}...</p>
    </div>
  );

  if (error) {
    console.error("GraphQL Error:", error);
    return (
      <div className="policy-error">
        <div className="error-icon">⚠️</div>
        <h3>Unable to load policies</h3>
        <p>{error.message}</p>
        <button onClick={() => refetch()} style={{ background: currentPolicy.color }}>Retry</button>
      </div>
    );
  }

  // Render
  return (
    <div className="policy-manager-compact">
      
      {notification.show && (
        <div className={`policy-notification ${notification.type}`}>
          <span className="notification-icon">{notification.type === "success" ? "✅" : "❌"}</span>
          <span className="notification-message">{notification.message}</span>
        </div>
      )}

      <div className="policy-container-compact">

        {/* Header */}
        <div className="policy-header-compact">
          <h2>📋 Policy Manager</h2>
          <button onClick={() => refetch()} className="refresh-btn-compact" style={{ color: currentPolicy.color }}>⟳</button>
        </div>

        {/* 5 Headings Row */}
        <div className="five-headings-row">
          {ALL_POLICIES.map((policy) => (
            <button
              key={policy.id}
              className={`heading-tab ${activePolicyId === policy.id ? 'active' : ''}`}
              onClick={() => handlePolicyChange(policy.id)}
              style={{
                background: activePolicyId === policy.id ? `linear-gradient(145deg, ${policy.color}, ${policy.color}dd)` : 'white',
                color: activePolicyId === policy.id ? 'white' : '#1E293B',
                borderColor: activePolicyId === policy.id ? policy.color : '#E2E8F0'
              }}
            >
              <span className="heading-icon">{policy.icon}</span>
              <span className="heading-label">{policy.label}</span>
              <span className="heading-badge" style={{
                background: activePolicyId === policy.id ? 'rgba(255,255,255,0.2)' : policy.lightColor,
                color: activePolicyId === policy.id ? 'white' : policy.color
              }}>
                {policy.badge}
              </span>
            </button>
          ))}
        </div>

        {/* Active Policy Banner */}
        {activeTerms && (
          <div className="active-mini-card" style={{ background: currentPolicy.lightColor, borderLeftColor: currentPolicy.color }}>
            <span className="active-mini-badge" style={{ background: currentPolicy.color }}>ACTIVE</span>
            <span className="active-mini-version">v{activeTerms.version}</span>
            <span className="active-mini-title">{activeTerms.title || currentPolicy.fullLabel}</span>
            <span className="active-mini-date">{formatDate(activeTerms.createdAt)}</span>
          </div>
        )}

        {/* Form Card – URL only */}
        <div className="form-card-compact">
          <div className="form-header-compact">
            <span className="form-icon-compact" style={{ background: currentPolicy.lightColor, color: currentPolicy.color }}>
              {editId ? "✏️" : currentPolicy.icon}
            </span>
            <span className="form-title-compact">{editId ? "Edit" : "New"} {currentPolicy.fullLabel}</span>
            {editId && <button onClick={resetForm} className="cancel-btn-compact">✕</button>}
          </div>

          <form onSubmit={handleSubmit} className="policy-form-compact">
            <input
              type="text"
              placeholder="Title (optional)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="form-input-compact"
              style={{ borderColor: currentPolicy.color }}
            />
            <input
              type="url"
              placeholder="Policy URL * (e.g., https://example.com/privacy)"
              value={termsUrl}
              onChange={(e) => setTermsUrl(e.target.value)}
              className="form-input-compact"
              style={{ borderColor: currentPolicy.color }}
              required
            />
            <button type="submit" className="submit-btn-compact" style={{ background: currentPolicy.color }}>
              {editId ? "✏️ Update" : "💾 Save"}
            </button>
          </form>
        </div>

        {/* Search */}
        <div className="search-compact">
          <div className="search-container-compact" style={{ borderColor: currentPolicy.color }}>
            <span className="search-icon-compact">🔍</span>
            <input
              type="text"
              placeholder={`Search ${currentPolicy.fullLabel.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input-compact"
            />
            {searchTerm && <button onClick={() => setSearchTerm("")} className="clear-search-compact">✕</button>}
          </div>
          <span className="stats-badge-compact" style={{ background: currentPolicy.lightColor, color: currentPolicy.color }}>
            {filteredTerms.length}
          </span>
        </div>

        {/* Version History – show URL chips instead of text preview */}
        <div className="versions-section">
          <div className="versions-header">
            <span className="versions-icon">📋</span>
            <span className="versions-title">Version History - {currentPolicy.fullLabel}</span>
            <span className="versions-count">{filteredTerms.length}</span>
          </div>
          
          {filteredTerms.length === 0 ? (
            <div className="empty-state-compact">
              <span className="empty-icon-compact">{currentPolicy.icon}</span>
              <p>No documents yet</p>
            </div>
          ) : (
            <div className="version-cards">
              {filteredTerms.map((term) => (
                <div 
                  key={term._id} 
                  className={`version-card ${term.isActive ? 'active' : ''}`}
                  style={{ 
                    borderColor: term.isActive ? currentPolicy.color : '#E2E8F0',
                    background: term.isActive ? currentPolicy.lightColor : 'white'
                  }}
                >
                  <div className="version-card-header">
                    <span className="version-number" style={{ background: currentPolicy.lightColor, color: currentPolicy.color }}>
                      v{term.version}
                    </span>
                    {term.isActive && <span className="version-active" style={{ background: currentPolicy.color }}>Current</span>}
                    <span className="version-date">{formatDate(term.createdAt)}</span>
                  </div>
                  <h5 className="version-title">{term.title || currentPolicy.fullLabel}</h5>
                  
                  {/* URL preview (if exists) */}
                  {term.termsUrl ? (
                    <div className="version-url">
                      <a href={term.termsUrl} target="_blank" rel="noopener noreferrer" className="url-chip" style={{ background: currentPolicy.lightColor, color: currentPolicy.color }}>
                        🔗 {term.termsUrl.replace(/^https?:\/\/(www\.)?/, "").split("/")[0]}
                      </a>
                    </div>
                  ) : (
                    <p className="version-preview">No URL set (fallback content)</p>
                  )}
                  
                  <div className="version-actions">
                    {!term.isActive && (
                      <button onClick={() => handleSetActive(term._id)} className="version-btn activate" style={{ color: currentPolicy.color }} title="Activate">✓</button>
                    )}
                    <button onClick={() => handleEdit(term)} className="version-btn edit" style={{ color: currentPolicy.color }} title="Edit">✏️</button>
                    <button onClick={() => handleDelete(term._id)} className="version-btn delete" title="Delete">🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PolicyManager;